import MainComputationCalculator from "../../../builders/calculator/mainComputation";
import { currencyRateConversion } from "../../../utils/hooks/dataHandler";
import { buildInvestCoMainCompSections } from "../Pdf/investco/buildInvestCoMainCompSections";
import { buildIrasMainCompSections } from "../Pdf/iras/buildIrasMainCompSections";
import { resolveIrasChargeableIncomeSummary } from "../Pdf/iras/chargeableIncomeSummary";
import {
  calculateIrasSgdSectionResults,
  getRenderableIrasMainCompSections,
} from "../Pdf/iras/sgdCalculation";
import { getSignedUnutilisedLossAmount } from "../Pdf/shared";
import { buildYASummary } from "../CrossYACalculationSummary";
import { getCrossYAApportionmentContext } from "./precedingCalculation";

const emptyRows = [];
const convertToSgd = (value, currency, currencyRate) => {
  const amount = Number(value || 0);
  if (String(currency || "SGD").toUpperCase() === "SGD") return amount;
  return Math.round(currencyRateConversion(amount, currencyRate || 1, "SGD"));
};

const normalizeUnutilisedLosses = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    if (row?.status !== "autoload") return row;

    const normalizedRow = {
      ...row,
      amount: getSignedUnutilisedLossAmount(row),
    };

    if (
      Object.prototype.hasOwnProperty.call(row, "preceding_amount") ||
      Object.prototype.hasOwnProperty.call(row, "current_amount")
    ) {
      normalizedRow.preceding_amount = getSignedUnutilisedLossAmount(
        row,
        "preceding_amount",
      );
      normalizedRow.current_amount = getSignedUnutilisedLossAmount(
        row,
        "current_amount",
      );
      normalizedRow.historyAmount = [
        normalizedRow.preceding_amount + normalizedRow.current_amount,
      ];
      normalizedRow.precedingHistoryAmount = [normalizedRow.preceding_amount];
      normalizedRow.currentHistoryAmount = [normalizedRow.current_amount];
    } else {
      normalizedRow.historyAmount = [normalizedRow.amount];
    }

    return normalizedRow;
  });

const normalizeSource = (doc = {}) => ({
  netProfitBeforeTax: doc.netProfitBeforeTax ?? 0,
  referenceData: doc.referenceData || {},
  unutilisedCapitalAllowances: doc.unutilisedCapitalAllowances || emptyRows,
  unutilisedLosses: normalizeUnutilisedLosses(doc.unutilisedLosses),
  unutilisedDonations: doc.unutilisedDonations || emptyRows,
  taxPreviouslyAssessed: doc.taxPreviouslyAssessed || emptyRows,
});

const isInvestment = (doc, computation) =>
  doc?.company?.companyType === "investment" ||
  /invest/i.test(
    computation?.taxComputationType || doc?.company?.taxComputationType || "",
  );

const caseRebate = (settings, fallback = {}) => ({
  ...fallback,
  taxableRate: Number(settings?.rebatePercentage ?? fallback.taxableRate ?? 0),
  cappedAmount: Number(settings?.rebateCap ?? fallback.cappedAmount ?? 0),
});

const getSectionAmount = (sections, id) => {
  const section = sections.find((item) => item.id === id);
  return Number(section?.amount ?? section?.total ?? 0);
};

const sectionLines = (sections) =>
  sections
    .filter((section) =>
      [
        "adjusted_profit_before_capital_allowances",
        "unutilised_ca_bf",
        "current_year_ca",
        "add_other_sourced_income",
        "unutilised_losses_bf",
        "current_year_donations",
        "unutilised_donations_bf",
        "total_investment_income",
        "current_year_losses",
      ].includes(section.id),
    )
    .map((section) => ({
      id: section.id,
      label: section.label,
      value: Number(section.amount ?? section.total ?? 0),
    }));

const resolveSingleYA = ({
  computation,
  doc,
  settings,
  groupReliefClaimSgd = 0,
}) => {
  const source = normalizeSource(doc);
  const investmentCompany = isInvestment(doc, computation);
  const currency = String(
    doc.currency || computation.currency || "SGD",
  ).toUpperCase();
  const currencyRate =
    Number(doc.currencyRate ?? computation.currency_rate ?? 1) || 1;
  const sections = investmentCompany
    ? buildInvestCoMainCompSections({
        computedSummary: doc.computedSummary,
        netProfitBeforeTax: source.netProfitBeforeTax,
        referenceData: source.referenceData,
      })
    : buildIrasMainCompSections({
        netProfitBeforeTax: source.netProfitBeforeTax,
        referenceData: source.referenceData,
        unutilisedCapitalAllowances: source.unutilisedCapitalAllowances,
        unutilisedDonations: source.unutilisedDonations,
        unutilisedLosses: source.unutilisedLosses,
      });

  let chargeableIncome;
  let sgdSections = null;
  if (investmentCompany) {
    chargeableIncome = Math.max(
      0,
      getSectionAmount(sections, "chargeable_income_before_exempt_amount"),
    );
    if (currency !== "SGD" && chargeableIncome > 0) {
      chargeableIncome = MainComputationCalculator.convertChargeableIncomeToSgd(
        chargeableIncome,
        currencyRate,
        [],
      );
    }
  } else if (currency === "SGD") {
    chargeableIncome = Math.max(
      0,
      getSectionAmount(sections, "total_after_donations"),
    );
  } else {
    const sgd = calculateIrasSgdSectionResults({
      currencyRate,
      sections: getRenderableIrasMainCompSections(sections),
      unutilisedCapitalAllowances: source.unutilisedCapitalAllowances,
      unutilisedDonations: source.unutilisedDonations,
      unutilisedLosses: source.unutilisedLosses,
    });
    // Display filtering can omit zero-activity checkpoints (e.g. assessable
    // income without donations). Allocation still needs those SGD balances.
    sgdSections = calculateIrasSgdSectionResults({
      currencyRate,
      sections,
      unutilisedCapitalAllowances: source.unutilisedCapitalAllowances,
      unutilisedDonations: source.unutilisedDonations,
      unutilisedLosses: source.unutilisedLosses,
    });
    chargeableIncome = sgd.chargeableIncomeBeforeExemptAmount ?? 0;
  }

  const taxSummary = resolveIrasChargeableIncomeSummary({
    chargeableIncomeBeforeExemptAmount: Math.max(
      0,
      Math.round(chargeableIncome) - groupReliefClaimSgd,
    ),
    corporateIncomeTaxRebate: caseRebate(
      settings,
      doc.corporateIncomeTaxRebate,
    ),
    currentYearRebate: doc.currentYearRebate,
    isStartup: doc.isStartup,
    taxPreviouslyAssessed: source.taxPreviouslyAssessed,
  });

  const amount = (id) => {
    const section = sections.find((item) => item.id === id);
    if (!section) return 0;
    if (sgdSections)
      return (
        (section.type === "result"
          ? sgdSections.resultAmounts
          : sgdSections.sectionAmounts
        ).get(section) ?? 0
      );
    const value = Number(section.amount ?? section.total ?? 0);
    return currency === "SGD"
      ? value
      : Math.round(
          MainComputationCalculator.convertChargeableIncomeToSgd(
            value,
            currencyRate,
            [],
          ),
        );
  };
  const adjustedProfitBeforeCapitalAllowances = amount(
    "adjusted_profit_before_capital_allowances",
  );
  const currentYearLosses = Math.min(
    0,
    adjustedProfitBeforeCapitalAllowances,
  );

  // Keep current-year availability in the same order as the Group Relief
  // optimiser: capital allowances are used against adjusted profit first;
  // only the remaining income (plus other-sourced income) is then available
  // for losses and donations. Using a later IRAS checkpoint here can let
  // other-sourced income consume CA capacity, which makes the optimiser move
  // the same amount as losses instead of transferring CA.
  const consumeDeduction = (balance, deduction) => {
    const availableDeduction = Math.abs(Math.min(0, Number(deduction) || 0));
    const used = Math.min(Math.max(0, balance), availableDeduction);
    return {
      nextBalance: balance - used,
      remaining: availableDeduction - used,
    };
  };

  const calculateCurrentYearAvailability = ({
    adjustedProfit,
    priorYearCA,
    currentYearCA,
    otherSourcedIncome,
    priorYearLosses,
    currentYearLosses: currentYearLossAmount,
    priorYearDonations,
    currentYearDonations,
  }) => {
    let profitBalance = Math.max(0, Number(adjustedProfit) || 0);
    const priorCA = consumeDeduction(profitBalance, priorYearCA);
    profitBalance = priorCA.nextBalance;
    const currentCA = consumeDeduction(profitBalance, currentYearCA);
    profitBalance = currentCA.nextBalance;

    let incomeBalance = Math.max(
      0,
      profitBalance + (Number(otherSourcedIncome) || 0),
    );
    const priorLosses = consumeDeduction(incomeBalance, priorYearLosses);
    incomeBalance = priorLosses.nextBalance;
    const currentLosses = consumeDeduction(
      incomeBalance,
      currentYearLossAmount,
    );
    incomeBalance = currentLosses.nextBalance;

    const priorDonations = consumeDeduction(
      incomeBalance,
      priorYearDonations,
    );
    incomeBalance = priorDonations.nextBalance;
    const currentDonations = consumeDeduction(
      incomeBalance,
      currentYearDonations,
    );

    return {
      currentYearCAAvailable: Math.floor(currentCA.remaining),
      currentYearLossesAvailable: Math.floor(currentLosses.remaining),
      currentYearDonationsAvailable: Math.floor(currentDonations.remaining),
    };
  };

  const detailedOutput = investmentCompany
    ? {
        adjustedProfit: 0,
        priorYearCA: 0,
        currentYearCA: 0,
        otherSourcedIncome: amount("total_investment_income"),
        priorYearLosses: 0,
        currentYearLosses: 0,
        priorYearDonations: 0,
        currentYearDonations: amount("current_year_donations"),
      }
    : {
        adjustedProfit: Math.max(0, adjustedProfitBeforeCapitalAllowances),
        priorYearCA: amount("unutilised_ca_bf"),
        currentYearCA: amount("current_year_ca"),
        otherSourcedIncome: amount("add_other_sourced_income"),
        priorYearLosses:
          amount("unutilised_losses_bf") - currentYearLosses,
        currentYearLosses,
        priorYearDonations: amount("unutilised_donations_bf"),
        currentYearDonations: amount("current_year_donations"),
      };
  const availability = calculateCurrentYearAvailability(detailedOutput);
  const utilisation = investmentCompany
    ? {
        ...availability,
        currentYearCAAvailable: 0,
        currentYearLossesAvailable: 0,
      }
    : availability;

  const groupRelief = {
    statutoryIncomeLessLosses: Math.max(
      0,
      amount(
        investmentCompany
          ? "total_investment_income"
          : "total_before_donations",
      ),
    ),
    assessableIncome: taxSummary.chargeableIncomeBeforeExemptAmount,
    utilisation,
    detailedOutput,
  };
  return {
    groupRelief,
    companyType: investmentCompany ? "investment" : "trading",
    taxPeriodType: "single_ya",
    currency,
    sourceLines: sectionLines(sections).map((line) => ({
      ...line,
      value: amount(line.id),
      currency: "SGD",
    })),
    periods: [
      {
        key: "current",
        label: `YA ${computation.yearOfAssessment}`,
        chargeableIncome: taxSummary.chargeableIncomeBeforeExemptAmount,
        taxPayable: taxSummary.netTaxPayable,
        taxPayableBeforeEci: taxSummary.taxPayableBeforeEci,
        taxExemption:
          taxSummary.firstExemptAmount + taxSummary.secondExemptAmount,
        taxBeforeRebate: Number(taxSummary.taxableIncome),
        citRebate: Number(taxSummary.rebate.netRebate),
        groupReliefClaimSgd,
      },
    ],
    chargeableIncome: taxSummary.chargeableIncomeBeforeExemptAmount,
    taxPayable: taxSummary.netTaxPayable,
    taxPayableBeforeEci: taxSummary.taxPayableBeforeEci,
  };
};

const resolveCrossYA = ({
  computation,
  doc,
  settings,
  groupReliefClaimSgd = 0,
}) => {
  const source = normalizeSource(doc);
  const investmentCompany = isInvestment(doc, computation);
  const company = {
    ...computation,
    ...doc.company,
    yearOfAssessment: computation.yearOfAssessment,
    financialYearStart:
      computation.financialYearStart || doc.company?.financialYearStart,
    financialYearEnd:
      computation.financialYearEnd || doc.company?.financialYearEnd,
    basisPeriods: computation.basisPeriods || doc.company?.basisPeriods,
    isCustomizedBasisPeriod:
      computation.isCustomizedBasisPeriod ??
      doc.company?.isCustomizedBasisPeriod ??
      false,
  };
  const context =
    doc.crossYAApportionmentContext ||
    getCrossYAApportionmentContext(company, true);
  if (!context) {
    throw new Error("The saved Cross-YA basis-period split is unavailable.");
  }

  const storedRebate = doc.corporateIncomeTaxRebate || {};
  const corporateIncomeTaxRebate = {
    preceding: storedRebate.preceding || storedRebate,
    current: caseRebate(settings, storedRebate.current || storedRebate),
  };
  const common = {
    company,
    context,
    corporateIncomeTaxRebate,
    currency: String(
      doc.currency || computation.currency || "SGD",
    ).toUpperCase(),
    currencyRate:
      Number(doc.currencyRate ?? computation.currency_rate ?? 1) || 1,
    currentYearRebate: doc.currentYearRebate,
    isInvestmentCompany: investmentCompany,
    isStartup: doc.isStartup,
    netProfitBeforeTax: source.netProfitBeforeTax,
    referenceData: source.referenceData,
    taxPreviouslyAssessed: source.taxPreviouslyAssessed,
    unutilisedCapitalAllowances: source.unutilisedCapitalAllowances,
    unutilisedDonations: source.unutilisedDonations,
    unutilisedLosses: source.unutilisedLosses,
  };
  const preceding = buildYASummary({
    ...common,
    amountKey: "preceding_amount",
    isCurrentYA: false,
  });
  const current = buildYASummary({
    ...common,
    amountKey: "current_amount",
    isCurrentYA: true,
    yearLossesBroughtForward: preceding.currentYearLosses,
    groupReliefClaimSgd,
  });
  const sgdSteps = (summary) =>
    summary.steps.map((step) => ({
      ...step,
      value:
        step.label === "Chargeable Income (Before Exempt)"
          ? Number(step.value || 0)
          : convertToSgd(step.value, common.currency, common.currencyRate),
      currency: "SGD",
    }));
  const periods = [
    {
      key: "preceding",
      label: `YA ${Number(computation.yearOfAssessment) - 1}`,
      chargeableIncome: preceding.chargebleIncomeBeforeExemptAmount,
      taxPayable: preceding.netTaxPayable,
      taxPayableBeforeEci: preceding.taxPayableBeforeEci,
      steps: sgdSteps(preceding),
    },
    {
      key: "current",
      label: `YA ${computation.yearOfAssessment}`,
      chargeableIncome: current.chargebleIncomeBeforeExemptAmount,
      taxPayable: current.netTaxPayable,
      taxPayableBeforeEci: current.taxPayableBeforeEci,
      steps: sgdSteps(current),
      taxExemption: current.totalExemptedAmount,
      taxBeforeRebate: Number(current.taxableIncome),
      citRebate: -Number(current.corporateRebate),
      groupReliefClaimSgd,
    },
  ];

  return {
    companyType: investmentCompany ? "investment" : "trading",
    taxPeriodType: "cross_ya",
    groupRelief: {
      ...current.groupRelief,
      statutoryIncomeLessLosses: current.groupRelief.assessableIncome,
      assessableIncome: current.chargebleIncomeBeforeExemptAmount,
      basisDays: context.currentDays,
      detailedOutput: current.groupReliefDetailedOutput,
    },
    currency: common.currency,
    sourceLines: [],
    periods,
    chargeableIncome: periods.reduce(
      (total, period) => total + Number(period.chargeableIncome || 0),
      0,
    ),
    taxPayable: periods.reduce(
      (total, period) => total + Number(period.taxPayable || 0),
      0,
    ),
    taxPayableBeforeEci: periods.reduce(
      (total, period) =>
        total + Number(period.taxPayableBeforeEci || 0),
      0,
    ),
  };
};

export const parseMainComputationSnapshot = (payload) => {
  const value = payload?.data || payload;
  const doc =
    typeof value?.doc === "string" ? JSON.parse(value.doc) : value?.doc;
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) {
    throw new Error("No saved Main Tax Computation snapshot is available.");
  }
  return {
    doc,
    savedAt: value.mainCompSavedAt || null,
  };
};

export const resolveMainComputationSnapshot = ({
  computation,
  doc,
  settings,
  groupReliefClaimSgd = 0,
}) =>
  computation.taxPeriodType === "cross_ya"
    ? resolveCrossYA({ computation, doc, settings, groupReliefClaimSgd })
    : resolveSingleYA({ computation, doc, settings, groupReliefClaimSgd });
